import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { reportsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import {
  Loader2,
  Download,
  Users,
  Zap,
  DollarSign,
  TrendingUp,
  FileText,
  FileDown,
  ChevronDown,
} from 'lucide-react';
import { toast } from 'sonner';

const Reports = () => {
  const { user } = useAuth();
  const [salesRepStats, setSalesRepStats] = useState([]);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [exportingPDF, setExportingPDF] = useState(false);
  const [exportingRepPDF, setExportingRepPDF] = useState(null);

  const loadReports = useCallback(async () => {
    try {
      const [repStatsRes, dashStatsRes] = await Promise.all([
        reportsAPI.getSalesRepReport(),
        reportsAPI.getDashboardStats(),
      ]);
      setSalesRepStats(repStatsRes.data);
      setDashboardStats(dashStatsRes.data);
    } catch (error) {
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const response = await reportsAPI.exportCSV();
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `leads_export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Export downloaded successfully');
    } catch (error) {
      toast.error('Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  const handleExportCompanyPDF = async () => {
    setExportingPDF(true);
    try {
      const response = await reportsAPI.exportCompanyPDF();
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `company_payout_report_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('PDF report downloaded successfully');
    } catch (error) {
      toast.error('Failed to export PDF report');
    } finally {
      setExportingPDF(false);
    }
  };

  const handleExportRepPDF = async (repId, repName) => {
    setExportingRepPDF(repId);
    try {
      const response = await reportsAPI.exportRepPDF(repId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `commission_statement_${repName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success(`Commission statement for ${repName} downloaded`);
    } catch (error) {
      toast.error('Failed to export commission statement');
    } finally {
      setExportingRepPDF(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="reports-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold text-slate-900 dark:text-white">
            Reports
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1">
            Performance analytics and business insights
          </p>
        </div>
        <div className="flex gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className="border-emerald-200 hover:bg-emerald-50 dark:border-emerald-800 dark:hover:bg-emerald-900/20"
                data-testid="export-dropdown-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Export
                <ChevronDown className="w-4 h-4 ml-2" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem
                onClick={handleExportCSV}
                disabled={exporting}
                data-testid="export-csv-menu-item"
              >
                <FileText className="w-4 h-4 mr-2" />
                {exporting ? 'Exporting...' : 'Export Leads (CSV)'}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={handleExportCompanyPDF}
                disabled={exportingPDF}
                data-testid="export-company-pdf-menu-item"
              >
                <FileDown className="w-4 h-4 mr-2 text-red-500" />
                {exportingPDF ? 'Generating PDF...' : 'Company Payout Report (PDF)'}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="stats-card" data-testid="stats-total-leads">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600 dark:text-slate-400">
                  Total Leads
                </p>
                <p className="text-3xl font-bold font-heading text-slate-900 dark:text-white mt-2">
                  {dashboardStats?.total_leads || 0}
                </p>
              </div>
              <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-xl">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stats-card" data-testid="stats-total-kw">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600 dark:text-slate-400">
                  Total kW Sold
                </p>
                <p className="text-3xl font-bold font-heading font-mono text-slate-900 dark:text-white mt-2">
                  {(dashboardStats?.total_kw_sold || 0).toFixed(1)}
                </p>
              </div>
              <div className="p-3 bg-amber-100 dark:bg-amber-900/30 rounded-xl">
                <Zap className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stats-card" data-testid="stats-commission">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600 dark:text-slate-400">
                  Total Commissions
                </p>
                <p className="text-3xl font-bold font-heading font-mono text-emerald-600 mt-2">
                  ${(dashboardStats?.total_commission_owed || 0).toLocaleString()}
                </p>
              </div>
              <div className="p-3 bg-emerald-100 dark:bg-emerald-900/30 rounded-xl">
                <DollarSign className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stats-card" data-testid="stats-nonprofit">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600 dark:text-slate-400">
                  SUNRAISING Donations
                </p>
                <p className="text-3xl font-bold font-heading font-mono text-rose-600 mt-2">
                  ${(dashboardStats?.total_nonprofit_donations || 0).toLocaleString()}
                </p>
              </div>
              <div className="p-3 bg-rose-100 dark:bg-rose-900/30 rounded-xl">
                <TrendingUp className="w-6 h-6 text-rose-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pipeline Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading">Pipeline Breakdown</CardTitle>
          <CardDescription>Leads distribution across milestones</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-9 gap-3">
            {Object.entries(dashboardStats?.leads_by_milestone || {}).map(([milestone, count]) => (
              <div
                key={milestone}
                className="text-center p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg"
              >
                <p className="text-2xl font-bold font-heading text-slate-900 dark:text-white">
                  {count}
                </p>
                <p className="text-xs text-slate-500 mt-1 truncate" title={milestone}>
                  {milestone}
                </p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Sales Rep Performance */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading">Sales Representative Performance</CardTitle>
          <CardDescription>Individual rep metrics and commissions</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {salesRepStats.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 dark:bg-slate-800/50">
                    <TableHead>Sales Rep</TableHead>
                    <TableHead className="text-center">Total Deals</TableHead>
                    <TableHead className="text-center">kW Sold</TableHead>
                    <TableHead className="text-right">Total Commission</TableHead>
                    <TableHead className="text-right">Paid</TableHead>
                    <TableHead className="text-right">Pending</TableHead>
                    <TableHead className="text-center">Statement</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {salesRepStats.map((rep) => (
                    <TableRow key={rep.sales_rep_id} data-testid={`rep-row-${rep.sales_rep_id}`}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center">
                            <span className="text-sm font-medium text-emerald-700">
                              {rep.sales_rep_name?.charAt(0)?.toUpperCase() || '?'}
                            </span>
                          </div>
                          <span className="font-medium text-slate-900 dark:text-white">
                            {rep.sales_rep_name}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant="secondary">{rep.total_deals}</Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="font-mono">{rep.total_kw.toFixed(1)}</span>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className="font-mono font-medium">
                          ${rep.total_commission.toLocaleString()}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className="font-mono text-emerald-600">
                          ${rep.commission_paid.toLocaleString()}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className="font-mono text-amber-600">
                          ${rep.commission_pending.toLocaleString()}
                        </span>
                      </TableCell>
                      <TableCell className="text-center">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleExportRepPDF(rep.sales_rep_id, rep.sales_rep_name)}
                          disabled={exportingRepPDF === rep.sales_rep_id}
                          data-testid={`export-rep-pdf-${rep.sales_rep_id}`}
                          title="Download Commission Statement"
                        >
                          {exportingRepPDF === rep.sales_rep_id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <FileDown className="w-4 h-4 text-red-500" />
                          )}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-16">
              <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300 dark:text-slate-600" />
              <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">
                No sales rep data
              </h3>
              <p className="text-slate-500 dark:text-slate-400">
                Sales rep performance data will appear when there are approved sales representatives with deals.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="font-heading">Commission Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-slate-600 dark:text-slate-400">Average per Rep</span>
                <span className="font-mono font-medium text-slate-900 dark:text-white">
                  ${salesRepStats.length > 0 
                    ? Math.round(salesRepStats.reduce((sum, r) => sum + r.total_commission, 0) / salesRepStats.length).toLocaleString()
                    : 0}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-slate-600 dark:text-slate-400">Average kW per Deal</span>
                <span className="font-mono font-medium text-slate-900 dark:text-white">
                  {dashboardStats?.total_leads > 0
                    ? ((salesRepStats.reduce((sum, r) => sum + r.total_kw, 0)) / 
                       (salesRepStats.reduce((sum, r) => sum + r.total_deals, 0) || 1)).toFixed(1)
                    : 0} kW
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-slate-600 dark:text-slate-400">Active Sales Reps</span>
                <span className="font-mono font-medium text-slate-900 dark:text-white">
                  {salesRepStats.filter(r => r.total_deals > 0).length}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="font-heading">SUNRAISING Impact</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-slate-600 dark:text-slate-400">Total Donations</span>
                <span className="font-mono font-medium text-rose-600">
                  ${(dashboardStats?.total_nonprofit_donations || 0).toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-slate-600 dark:text-slate-400">Donation per Eligible Sale</span>
                <span className="font-mono font-medium text-slate-900 dark:text-white">
                  $500
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-slate-600 dark:text-slate-400">Program Status</span>
                <Badge className="bg-emerald-100 text-emerald-700">Active</Badge>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Reports;
